import React, { useState } from 'react';
import {
  MONTH_CELL_PART_OPTIONS,
  MONTH_HEATMAP_OPTIONS,
  WEEK_STATUS_OPTIONS,
  type CalendarMonthHeatmap,
} from '../../calendar/filters';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Segmented } from '../Segmented/Segmented';
import type { CalendarTimeDensity, CalendarViewMode } from '../../calendar/types';
import { ViewportDrawer } from '../Overlay/ViewportDrawer';
import { CalendarDayOptionsDrawer } from './CalendarDayOptionsDrawer';

const eyebrowClass =
  'font-[var(--font-mono)] text-[9.5px] font-semibold uppercase tracking-[0.12em] text-[var(--ink-3)]';

function CheckRow({
  label,
  sub,
  dot,
  defaultChecked,
  checked,
  onChange,
}: {
  label: string;
  sub?: string;
  dot?: string;
  defaultChecked?: boolean;
  /** Controlled mode — when `onChange` is given, `checked` drives the state. */
  checked?: boolean;
  onChange?: () => void;
}) {
  const [localOn, setLocalOn] = useState(!!defaultChecked);
  const on = onChange ? !!checked : localOn;
  const toggle =
    onChange ??
    (() => {
      setLocalOn(!localOn);
    });
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter') toggle();
      }}
      className="flex cursor-pointer items-center gap-2 rounded-md p-1.5"
    >
      <span
        className={cn(
          'inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] text-white',
          on
            ? 'border-[1.5px] border-[var(--sage-0)] bg-[var(--sage-0)]'
            : 'border-[1.5px] border-[var(--cream-shadow)] bg-[var(--cream-1)]',
        )}
      >
        {on && <Icon kind="check" size={11} stroke={3} />}
      </span>
      {dot && <span className="h-[7px] w-[7px] shrink-0 rounded-full" style={{ background: dot }} />}
      <span className="min-w-0 flex-1">
        <span className="block text-[12.5px] text-[var(--ink-1)]">{label}</span>
        {sub && <span className="mt-px block text-[10.5px] text-[var(--ink-4)]">{sub}</span>}
      </span>
    </div>
  );
}

export interface CalendarOptionsDrawerProps {
  open: boolean;
  onClose: () => void;
  quick: string;
  onQuickChange: (v: string) => void;
  large: boolean;
  onLargeChange: (v: boolean) => void;
  /** Day view — provider vs operatory columns. */
  viewMode?: CalendarViewMode;
  onViewModeChange?: (mode: CalendarViewMode) => void;
  /** Day view — hour block height. */
  timeDensity?: CalendarTimeDensity;
  onTimeDensityChange?: (density: CalendarTimeDensity) => void;
  productionThreshold?: number;
  onProductionThresholdChange?: (value: number) => void;
  /** Day view — real provider columns + hidden set for the Providers filter. */
  providers?: ReadonlyArray<{ id: string; name: string; role: string }>;
  hiddenProviders?: ReadonlySet<string>;
  onToggleProvider?: (id: string) => void;
  onResetDayOptions?: () => void;
  /** Week view — date range + hide day-only controls. */
  weekMode?: boolean;
  /** Month view — heatmap + highlight filters. */
  monthMode?: boolean;
  /** Resets every filter of the current view. Falls back to setting the quick filter to `all`. */
  onReset?: () => void;
  /** Day view — status / operatory / flag filters (see CalendarDayOptionsDrawer). */
  onResetProviders?: () => void;
  hiddenStatuses?: ReadonlySet<string>;
  onToggleStatus?: (id: string) => void;
  onResetStatus?: () => void;
  operatories?: ReadonlyArray<{ id: string; name: string; role?: string }>;
  hiddenOperatories?: ReadonlySet<string>;
  onToggleOperatory?: (id: string) => void;
  onResetOperatories?: () => void;
  selectedFlags?: ReadonlySet<string>;
  onToggleFlag?: (id: string) => void;
  onResetFlags?: () => void;
  /** "Showing X of Y" for the active view's filters. */
  resultSummary?: { matched: number; total: number };
  /** Week view — status ids (`confirmed`, `pending`, `no_show`, `in_treatment`) the user un-ticked. */
  weekHiddenStatuses?: ReadonlySet<string>;
  onToggleWeekStatus?: (id: string) => void;
  /** Week view — window length. The control only shows when a change handler is passed. */
  weekRangeDays?: 7 | 14;
  onWeekRangeChange?: (days: 7 | 14) => void;
  /** Month view — heatmap metric and the cell parts the user un-ticked. */
  monthHeatmap?: CalendarMonthHeatmap;
  onMonthHeatmapChange?: (heatmap: CalendarMonthHeatmap) => void;
  monthHiddenCellParts?: ReadonlySet<string>;
  onToggleMonthCellPart?: (id: string) => void;
}

export function CalendarOptionsDrawer({
  open,
  onClose,
  quick,
  onQuickChange,
  large,
  onLargeChange,
  viewMode = 'Provider',
  onViewModeChange,
  timeDensity = 'Small',
  onTimeDensityChange,
  productionThreshold = 0,
  onProductionThresholdChange,
  providers,
  hiddenProviders,
  onToggleProvider,
  onResetDayOptions,
  weekMode = false,
  monthMode = false,
  onReset,
  onResetProviders,
  hiddenStatuses,
  onToggleStatus,
  onResetStatus,
  operatories,
  hiddenOperatories,
  onToggleOperatory,
  onResetOperatories,
  selectedFlags,
  onToggleFlag,
  onResetFlags,
  resultSummary,
  weekHiddenStatuses,
  onToggleWeekStatus,
  weekRangeDays = 7,
  onWeekRangeChange,
  monthHeatmap = 'goal',
  onMonthHeatmapChange,
  monthHiddenCellParts,
  onToggleMonthCellPart,
}: CalendarOptionsDrawerProps) {
  if (!weekMode && !monthMode) {
    return (
      <CalendarDayOptionsDrawer
        open={open}
        onClose={onClose}
        quick={quick}
        onQuickChange={onQuickChange}
        large={large}
        onLargeChange={onLargeChange}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange ?? (() => undefined)}
        timeDensity={timeDensity}
        onTimeDensityChange={onTimeDensityChange ?? (() => undefined)}
        productionThreshold={productionThreshold}
        onProductionThresholdChange={onProductionThresholdChange ?? (() => undefined)}
        {...(providers ? { providers } : {})}
        {...(hiddenProviders ? { hiddenProviders } : {})}
        {...(onToggleProvider ? { onToggleProvider } : {})}
        {...(onResetProviders ? { onResetProviders } : {})}
        {...(hiddenStatuses ? { hiddenStatuses } : {})}
        {...(onToggleStatus ? { onToggleStatus } : {})}
        {...(onResetStatus ? { onResetStatus } : {})}
        {...(operatories ? { operatories } : {})}
        {...(hiddenOperatories ? { hiddenOperatories } : {})}
        {...(onToggleOperatory ? { onToggleOperatory } : {})}
        {...(onResetOperatories ? { onResetOperatories } : {})}
        {...(selectedFlags ? { selectedFlags } : {})}
        {...(onToggleFlag ? { onToggleFlag } : {})}
        {...(onResetFlags ? { onResetFlags } : {})}
        {...(resultSummary ? { resultSummary } : {})}
        onReset={onResetDayOptions ?? onReset ?? (() => onQuickChange('all'))}
      />
    );
  }

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'yr', label: 'Yellow & Red' },
    { id: 'red', label: 'Red only' },
  ];

  return (
    <ViewportDrawer open={open} onClose={onClose} width="min(420px, 92vw)">
      <div
        className="flex h-full flex-col"
        style={{ background: '#F1F4F8' }}
      >
        <div className="flex items-center gap-2.5 border-b border-[var(--cream-2)] px-5 py-4">
          <Icon kind="filter" size={14} color="var(--ink-3)" />
          <span className="font-[var(--font-display)] text-[15px] font-semibold text-[var(--ink-0)]">
            Options
          </span>
          {resultSummary ? (
            <span className="font-[var(--font-mono)] text-[11px] text-[var(--ink-3)]">
              {resultSummary.matched} of {resultSummary.total}
            </span>
          ) : null}
          <span className="ml-auto">
            <IconButton icon="x" size={30} onClick={onClose} label="Close" />
          </span>
        </div>
        <div className="cal-scroll flex-1 overflow-auto px-5 pb-6 pt-4">
          {monthMode ? (
            <>
              <div className="mb-4">
                <div className={cn(eyebrowClass, 'mb-2')}>Day heatmap</div>
                <Segmented
                  options={MONTH_HEATMAP_OPTIONS.map((o) => ({ value: o.id, label: o.label }))}
                  value={monthHeatmap}
                  onChange={(v) => onMonthHeatmapChange?.(v as CalendarMonthHeatmap)}
                />
              </div>
              <div className="my-3.5 h-px bg-[var(--cream-2)]" />
              <div className="mb-4">
                <div className={cn(eyebrowClass, 'mb-2')}>Highlight days</div>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { id: 'all', label: 'All days' },
                    { id: 'under', label: 'Under goal' },
                    { id: 'issues', label: 'Issues only' },
                  ].map((o) => {
                    const active = quick === o.id;
                    return (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => {
                          onQuickChange(o.id);
                        }}
                        className={cn(
                          'cursor-pointer rounded-[var(--r-pill)] px-3 py-1.5 font-[var(--font-body)] text-xs font-semibold',
                          active
                            ? 'border border-[var(--ai-3)] bg-[var(--ai-tint)] text-[var(--ai-3)]'
                            : 'border border-[var(--cream-2)] bg-[var(--cream-1)] text-[var(--ink-2)]',
                        )}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          ) : onWeekRangeChange ? (
            <div className="mb-4">
              <div className={cn(eyebrowClass, 'mb-2')}>Date range</div>
              <Segmented
                options={[
                  { value: '7', label: 'Next 7 days' },
                  { value: '14', label: 'Next 14 days' },
                ]}
                value={String(weekRangeDays)}
                onChange={(v) => onWeekRangeChange(v === '14' ? 14 : 7)}
              />
            </div>
          ) : null}
          <div className="my-3.5 h-px bg-[var(--cream-2)]" />
          {!monthMode ? (
            <div className="mb-4">
              <div className={cn(eyebrowClass, 'mb-2')}>Readiness filter</div>
              <div className="flex flex-wrap gap-1.5">
                {filters.map((o) => {
                  const active = quick === o.id;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => {
                        onQuickChange(o.id);
                      }}
                      className={cn(
                        'cursor-pointer rounded-[var(--r-pill)] px-3 py-1.5 font-[var(--font-body)] text-xs font-semibold',
                        active
                          ? 'border border-[var(--ai-3)] bg-[var(--ai-tint)] text-[var(--ai-3)]'
                          : 'border border-[var(--cream-2)] bg-[var(--cream-1)] text-[var(--ink-2)]',
                      )}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
          <div className="my-3.5 h-px bg-[var(--cream-2)]" />
          <div className="overflow-hidden rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)]">
            <div className="border-b border-[var(--cream-2)] px-3 py-2.5 text-[12.5px] font-semibold text-[var(--ink-0)]">
              {monthMode ? 'Show on cells' : 'Appointment status'}
            </div>
            <div className="px-2.5 py-2">
              {monthMode
                ? MONTH_CELL_PART_OPTIONS.map((part) => (
                    <CheckRow
                      key={part.id}
                      label={part.label}
                      dot={part.dot}
                      defaultChecked
                      {...(onToggleMonthCellPart
                        ? {
                            checked: !monthHiddenCellParts?.has(part.id),
                            onChange: () => {
                              onToggleMonthCellPart(part.id);
                            },
                          }
                        : {})}
                    />
                  ))
                : WEEK_STATUS_OPTIONS.map((status) => (
                    <CheckRow
                      key={status.id}
                      label={status.label}
                      dot={status.dot}
                      defaultChecked
                      {...(onToggleWeekStatus
                        ? {
                            checked: !weekHiddenStatuses?.has(status.id),
                            onChange: () => {
                              onToggleWeekStatus(status.id);
                            },
                          }
                        : {})}
                    />
                  ))}
            </div>
          </div>
        </div>
        <div className="flex gap-2 border-t border-[var(--cream-2)] bg-[var(--cream-1)] px-5 py-3">
          <Button
            variant="secondary"
            className="flex-1"
            onClick={onReset ?? (() => onQuickChange('all'))}
          >
            Reset
          </Button>
          <Button variant="primary" className="flex-1" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </ViewportDrawer>
  );
}
